import React, { createContext, useContext, useState } from 'react';
import type { FC, ReactNode } from 'react';
import pLimit from 'p-limit';
import { match } from 'variant';
import { Trans } from '@wix/cli-i18n';
import { openBrowser } from '@wix/user-browser';
import { Spinner, useAsync, useExit } from '@wix/cli-ui-kit';
import { useProjectModel } from '../../model.js';
import { createSiteFsManager } from '../../site-fs-manager.js';
import { createIntellisenseAdapter } from '../../intellisense-adapter.js';
import { useVelo } from './useVelo.js';
import { useFsWatcher } from './useFsWatcher.js';
import { useDevEditorUrl } from './useDevEditorUrl.js';
import { useWebSocketServer } from './useWebSocketServer.js';

interface DevEnvironment {
  devEditorUrl: string;
  isSyncingChanges: boolean;
  isDevEditorConnected: boolean;
}

const DevEnvironmentContext = createContext<DevEnvironment | null>(null);

export const DevEnvironmentProvider: FC<{
  children: ReactNode;
  useTunnel: boolean;
}> = ({ children, useTunnel: shouldUseTunnel }) => {
  const exit = useExit();
  const { model } = useProjectModel();
  const { createMutableApp, updateAppFiles } = useVelo();
  const getDevEditorUrl = useDevEditorUrl();
  const wsServer = useWebSocketServer();
  const fsWatcher = useFsWatcher(model.projectFolder);

  const [isSyncing, setIsSyncing] = useState(false);
  const [isDevEditorConnected, setIsDevEditorConnected] = useState(false);

  const { status } = useAsync(async () => {
    const limiter = pLimit(1);
    const siteFsManager = await createSiteFsManager(model.projectFolder);
    const intellisenseAdapter = await createIntellisenseAdapter(
      model.projectFolder,
      siteFsManager.convertToOnlineLayout
    );

    const [code, document] = await Promise.all([
      siteFsManager.getCode(),
      siteFsManager.getDocument(),
    ]);

    const [{ id: appId }] = await Promise.all([
      createMutableApp(code),
      intellisenseAdapter.initTypes(document),
    ]);

    wsServer.on('connected', () => {
      setIsDevEditorConnected(true);
    });
    wsServer.on('disconnected', () => {
      setIsDevEditorConnected(false);
    });
    wsServer.on('documentUpdate', async (document) => {
      try {
        await limiter(async () => {
          setIsSyncing(true);
          await siteFsManager.applyExternalUpdates({ document });
          await intellisenseAdapter.updateTypes(document);
        });
      } catch (error) {
        exit(error);
      } finally {
        setIsSyncing(false);
      }
    });

    const { port, wsUrl } = await wsServer.serve(appId, {
      getDocument: siteFsManager.getDocument,
      onConnectionError: exit,
    });

    fsWatcher.onBatchChange(async (update) => {
      try {
        await limiter(async () => {
          setIsSyncing(true);

          const { document, code } = await siteFsManager.applyFsUpdates(update);

          await Promise.all([
            code && updateAppFiles(appId, code),
            document && wsServer.syncDocumentUpdate(document),
            document && intellisenseAdapter.updateTypes(document),
          ]);
        });
      } catch (error) {
        exit(error);
      } finally {
        setIsSyncing(false);
      }
    });

    const devEditorUrl = await getDevEditorUrl({
      serverWsUrl: wsUrl,
      serverPort: port,
      shouldUseTunnel,
    });

    await openBrowser(devEditorUrl);

    return { devEditorUrl };
  }, []);

  return match(status, {
    Error: () => null,
    Loading: () => (
      <Spinner text={<Trans i18nKey="dev_command.dev_environment.loading" />} />
    ),
    Success: ({ result: { devEditorUrl } }) => (
      <DevEnvironmentContext.Provider
        value={{
          devEditorUrl,
          isDevEditorConnected,
          isSyncingChanges: isSyncing,
        }}
      >
        {children}
      </DevEnvironmentContext.Provider>
    ),
  });
};

export function useDevEnvironment(): DevEnvironment {
  const context = useContext(DevEnvironmentContext);
  if (context == null) {
    throw new Error(
      'useDevEnvironment() cannot be used without an <DevEnvironmentProvider>'
    );
  }
  return context;
}
